import rest from "@/rest-client";

export const workspace = {
  moduleName: "workspace",
  path: "/workspace",

  namespaced: true,
  state: () => ({
    purchasedFeatures: undefined,
    showInviteModal: false,
    paginationMember: undefined,
    pagination: undefined,
    workspaces: [],
    organizations: [],
    members: [],
    isLoading: false,
    isUpdating: false,
    memberInfo: undefined,
    isMemberInfoLoading: false,
    editedEntity: undefined,
    paginateWorkspaceOrganization: undefined,
  }),
  getters: {
    workspaces: (state) => state.workspaces,
    purchasedFeatures: (state) => state.purchasedFeatures,
    organizations: (state) => state.organizations,
    members: (state) => state.members,
    pagination: (state) => state.pagination,
    paginationMember: (state) => state.paginationMember,
    showInviteModal: (state) => state.showInviteModal,
    paginateWorkspaceOrganization: (state) => state.paginateWorkspaceOrganization,

    roleByMemberId: (state) => (id) => {
      return state.paginationMember.docs.filter((member) => member._id === id)[0].workspaceRole;
    },
    isLoading: (state) => state.isLoading,
    isUpdating: (state) => state.isUpdating,
    memberInfo: (state) => state.memberInfo,
    isMemberInfoLoading: (state) => state.isMemberInfoLoading,
    editedEntity: (state) => state.editedEntity ?? {},
  },

  mutations: {
    paginateWorkspaceOrganization: (state, value) => (state.paginateWorkspaceOrganization = value),
    memberRole: (state, { member, role }) => {
      const memberIndex = state.paginationMember.docs.findIndex((item) => item._id === member._id);
      if (memberIndex !== -1) {
        const updatedMember = Object.assign({}, state.paginationMember.docs[memberIndex]);
        updatedMember.workspaceRole = role;
        state.paginationMember.docs.splice(memberIndex, 1, updatedMember);
        return updatedMember.workspaceRole;
      }
    },
    memberEdited: (state, { member, edited }) => {
      const memberIndex = state.paginationMember.docs.findIndex((item) => item._id === member._id);
      if (memberIndex !== -1) {
        const updatedMember = Object.assign({}, state.paginationMember.docs[memberIndex]);
        updatedMember.edited = edited;
        state.paginationMember.docs.splice(memberIndex, 1, updatedMember);
      }
    },

    workspaces: (state, value) => (state.workspaces = value),
    purchasedFeatures: (state, value) => (state.workspacePurchasedFeatures = value),
    organizations: (state, value) => (state.organizations = value),
    members: (state, value) => (state.members = value),
    isLoading: (state, value) => (state.isLoading = value),
    isUpdating: (state, value) => (state.isUpdating = value),
    memberInfo: (state, value) => (state.memberInfo = value),
    isMemberInfoLoading: (state, value) => (state.isMemberInfoLoading = value),
    editedEntity: (state, value) => (state.editedEntity = value),
    paginationMember: (state, value) => (state.paginationMember = value),
    pagination: (state, value) => (state.pagination = value),
    showInviteModal: (state, value) => (state.showInviteModal = value),
  },

  actions: {
    async create({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.post("/workspace", payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async duplicate({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.post("/workspace/duplicate", payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async delete({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.post("/workspace/delete", payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async updateName({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.put("/workspace", payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async fetchWorkspaceDetail({ commit }, id) {
      commit("isLoading", true);
      try {
        const response = await rest.get(`/workspace/${id}`);
        return response;
      } finally {
        commit("isLoading", false);
      }
    },

    async fetchWorkspaceOrganizations({ commit }, { id, request }) {
      commit("isLoading", true);
      try {
        const response = await rest.post(`/workspace/${id}/organizations`, { workspaceId: id, pagination: request });

        commit("paginateWorkspaceOrganization", response.data);

        return response;
      } finally {
        commit("isLoading", false);
      }
    },

    async fetchWorkspaceMemberInfo({ commit }, id) {
      commit("isMemberInfoLoading", true);
      try {
        const response = await rest.get(`/workspace/member/${id}`);

        commit("memberInfo", response.data);

        return response;
      } finally {
        commit("isMemberInfoLoading", false);
      }
    },

    async setMemberOrganizationAcl({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.put(`/workspace/member-acl/set`, payload);

        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async invite({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.post(`/workspace/invite`, payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async updateMemberWorkspaceRole({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.put("/workspace/member", payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async removeMember({ commit }, id) {
      commit("isUpdating", true);
      try {
        const response = await rest.delete(`/workspace/member/${id}`);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },
    async activateMember({ commit }, id) {
      commit("isUpdating", true);
      try {
        const response = await rest.get(`/workspace/member/${id}/activate`);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async paginateMember({ commit, state }, { pagination, id }) {
      commit("isLoading", true);
      try {
        const response = await rest.post(`workspace/${id}/members/paginate`, { pagination });
        commit("paginationMember", response.data);
        return response;
      } finally {
        commit("isLoading", false);
      }
    },

    async paginate({ commit }, payload) {
      commit("isLoading", true);
      try {
        const response = await rest.post(`/workspace/paginate`, payload);
        commit("pagination", response.data);
        return response;
      } finally {
        commit("isLoading", false);
      }
    },
  },
};
